/**
 * Add / edit a vault item.
 *
 * The secret field is write-only by design: editing an existing item never loads
 * its plaintext into this form. Leaving the field blank keeps whatever is stored
 * (the API treats an absent `secret` key as "don't touch it"), and removing a
 * secret is an explicit, separate choice.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { VaultItem } from '../../shared/types';

interface Props {
  /** null = add mode. */
  item: VaultItem | null;
  /** Categories already in use, so the household's own vocabulary is suggested back. */
  categories: string[];
  encryptionReady: boolean;
  onSaved: (item: VaultItem) => void;
  onClose: () => void;
}

interface Payload {
  title: string;
  category: string | null;
  username: string | null;
  url: string | null;
  notes: string | null;
  /** Absent = leave the stored secret alone. '' = remove it. */
  secret?: string;
}

const FIELD =
  'h-11 w-full rounded-xl border border-border bg-surface px-3 text-sm text-ink placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none';
const LABEL = 'mb-1 block text-sm font-medium text-ink';

export default function VaultItemDialog({
  item,
  categories,
  encryptionReady,
  onSaved,
  onClose,
}: Props) {
  const editing = item !== null;
  const uid = useId();

  const [title, setTitle] = useState(item?.title ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [username, setUsername] = useState(item?.username ?? '');
  const [url, setUrl] = useState(item?.url ?? '');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [secret, setSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [removeSecret, setRemoveSecret] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panel = useRef<HTMLDivElement>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  // Escape closes; Tab stays inside the dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panel.current) return;
      const focusable = panel.current.querySelectorAll<HTMLElement>(
        'button, input, textarea, a[href], [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError('Give this item a title so you can find it later.');
      firstField.current?.focus();
      return;
    }

    const payload: Payload = {
      title: trimmedTitle,
      category: category.trim() || null,
      username: username.trim() || null,
      url: url.trim() || null,
      notes: notes.trim() || null,
    };

    if (removeSecret) payload.secret = '';
    else if (secret) payload.secret = secret;

    setSaving(true);
    setError(null);
    try {
      const saved = editing
        ? await api.put<VaultItem>(`/vault/${item.id}`, payload)
        : await api.post<VaultItem>('/vault', payload);
      // Drop the plaintext from component memory the moment it's been handed off.
      setSecret('');
      onSaved(saved);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? (err.body.detail ?? err.body.error)
          : "That couldn't be saved. Nothing was changed."
      );
      setSaving(false);
    }
  }

  const secretHelp = !encryptionReady
    ? "Secrets can't be saved until the encryption key is configured."
    : editing && item.has_secret
      ? 'Leave blank to keep the secret that\'s already saved.'
      : 'Encrypted in the Worker before it reaches the database.';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-ink/30 p-4 backdrop-blur-sm sm:p-8">
      {/* Click-away. A real button so it isn't a click handler on a div. */}
      <button
        type="button"
        aria-label="Close without saving"
        tabIndex={-1}
        onClick={onClose}
        className="fixed inset-0 h-full w-full cursor-default"
      />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${uid}-heading`}
        className="relative mx-auto w-full max-w-lg rounded-card border border-border bg-surface p-5 shadow-xl sm:p-6"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={`${uid}-heading`} className="font-display text-xl font-semibold text-ink">
            {editing ? 'Edit item' : 'Add to the vault'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-m-2 grid h-11 w-11 place-items-center rounded-full text-ink-faint hover:bg-surface-sunk hover:text-ink focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
            aria-label="Close without saving"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {error && (
          <p
            role="alert"
            className="mb-4 rounded-xl border border-clay/40 bg-clay-soft px-3 py-2 text-sm text-ink"
          >
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor={`${uid}-title`} className={LABEL}>
              Title <span className="text-ink-faint">(required)</span>
            </label>
            <input
              ref={firstField}
              id={`${uid}-title`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Wi-Fi — Mangum House"
              maxLength={120}
              className={FIELD}
              required
            />
          </div>

          <div>
            <label htmlFor={`${uid}-category`} className={LABEL}>
              Category
            </label>
            <input
              id={`${uid}-category`}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              list={`${uid}-categories`}
              placeholder="Utility, Account, Code, Provider…"
              className={FIELD}
            />
            <datalist id={`${uid}-categories`}>
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <p className="mt-1 text-xs text-ink-faint">
              Items are grouped by category. Type anything — the list learns from what you use.
            </p>
          </div>

          <div>
            <label htmlFor={`${uid}-username`} className={LABEL}>
              Username or account number
            </label>
            <input
              id={`${uid}-username`}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              className={FIELD}
            />
          </div>

          <div>
            <label htmlFor={`${uid}-secret`} className={LABEL}>
              Secret <span className="text-ink-faint">(password, code, PIN)</span>
            </label>
            <div className="flex gap-2">
              <input
                id={`${uid}-secret`}
                type={showSecret ? 'text' : 'password'}
                value={secret}
                onChange={(e) => {
                  setSecret(e.target.value);
                  if (e.target.value) setRemoveSecret(false);
                }}
                disabled={!encryptionReady || removeSecret}
                autoComplete="new-password"
                spellCheck={false}
                maxLength={4096}
                placeholder={editing && item.has_secret ? '••••••••••' : ''}
                aria-describedby={`${uid}-secret-help`}
                className={`${FIELD} font-mono disabled:cursor-not-allowed disabled:bg-surface-sunk`}
              />
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                disabled={!secret}
                className="h-11 shrink-0 rounded-xl border border-border px-3 text-xs font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none disabled:opacity-40"
              >
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
            <p id={`${uid}-secret-help`} className="mt-1 text-xs text-ink-faint">
              {secretHelp}
            </p>

            {editing && item.has_secret && (
              <label className="mt-2 flex min-h-11 items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={removeSecret}
                  onChange={(e) => {
                    setRemoveSecret(e.target.checked);
                    if (e.target.checked) setSecret('');
                  }}
                  className="h-4 w-4 rounded border-border text-clay focus-visible:ring-2 focus-visible:ring-clay focus-visible:outline-none"
                />
                Remove the saved secret from this item
              </label>
            )}
          </div>

          <div>
            <label htmlFor={`${uid}-url`} className={LABEL}>
              Link
            </label>
            <input
              id={`${uid}-url`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              inputMode="url"
              placeholder="duke-energy.com"
              className={FIELD}
            />
          </div>

          <div>
            <label htmlFor={`${uid}-notes`} className={LABEL}>
              Notes
            </label>
            <textarea
              id={`${uid}-notes`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Account #, who to call, where the panel is…"
              className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
            />
            <p className="mt-1 text-xs text-ink-faint">
              Notes are stored as written — keep passwords in the Secret field so they're encrypted.
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 items-center justify-center rounded-full border border-border bg-surface px-5 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex h-11 items-center justify-center rounded-full bg-sage px-6 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:outline-none disabled:opacity-60"
            >
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add to vault'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
